/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
} from 'antd';
import {
  CheckCircleOutlined, InfoCircleOutlined
  ,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import {
  ModelInfoConfigGuardVacaGeneral,
} from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesGeneral';
import { getModelPermission } from 'utils/permission';
import ConfigurarGuardiasVacacionalesPeriodos from './ConfigurarGuardiasVacacionalesPeriodos';
import ConfigurarGuardiasVacacionalesExcepciones from './ConfigurarGuardiasVacacionalesExcepciones';

const modelPermissionName = 'configurarguardiasvacacionalesgeneral';

export const configurarGuardiasVacacionalesGeneralPermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [],
  tabsModels: [],
};

const ConfigurarGuardiasVacacionalesGeneral = ({ permission }) => {
  const tab1 = 'periodos';
  const tab2 = 'excepciones';
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [open, setVisible] = useState(false);
  const [currentParentTabKey, setCurrentParentTabKey] = useState(tab1);
  const [estados, setEstados] = useState([]);
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const { TabPane } = Tabs;
  const registroName = `${selectedData?.periodo_fiscal?.periodo}`;
  const {
    cliente,
    nameModel,
    nameModelSingular,
    formatter,
  } = ModelInfoConfigGuardVacaGeneral;

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _periodosFiscales,
        _estados,
        _data,
      ] = await Promise.all([
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getEstadosGlobales(),
        cliente.obtenerListaDeRegistros(),
      ]);
      if (mounted) {
        setPeriodosFiscales(_periodosFiscales);
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };
  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;

      const values = formatter.formatRecordForSend(valuesForm);
      const saved = await (
        key
          ? cliente.actualizarRegistro(key, values)
          : cliente.crearRegistro(values)
      );
      if (saved) {
        if (_continue) {
          await fetchData();
          handleOnRowClick(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickAdd = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      form.resetFields();
      setVisible(true);
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onClickEdit = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      form.resetFields();
      form.setFieldsValue(formatter.formatRecordForRead(selectedData));
      setDisableFields(selectedData?.estados_globales?.id !== ESTATUS_ACTIVO);
      setVisible(true);
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, `No se puede autorizar ${nameModelSingular} ya autorizado`);
        return;
      }
      const saved = await cliente.autorizarRegistro(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Año',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 100,
      render: (_, record) => record?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado',
      key: 'estado',
      width: 80,
      render: (_, record) => record?.estados_globales?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            customActions={[
              {
                onClick: () => setVisibleAutorizar(true),
                permission: { model: 'configurarguardiasvacacionalesgeneral', action: 'autorizar' },
                icon: <CheckCircleOutlined />,
                text: 'Autorizar',
                disabledBySelection: true,
                disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                className: 'success',
              },
            ]}
            permission={permission}
            allowExportParam={false}
          />
        )
          : (
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'EDITAR' : 'AGREGAR'} ${nameModel} `}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onCancel={onCancel}
                  loading={loading}
                  onFinishText="Guardar y Continuar"
                />
              )}
              bordered={false}
            >
              <Form
                layout="vertical"
                form={form}
                scrollToFirstError
                name="general"
                initialValues={{
                  periodo_fiscal: periodosFiscales[periodosFiscales.length - 1]?.id,
                  estados_globales: ESTATUS_ACTIVO,
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="periodo_fiscal"
                      label="Año"
                      rules={rules.estatus}
                      hasFeedback
                    >
                      <Select
                        dataSource={periodosFiscales}
                        disabled={disableFields}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      hasFeedback
                    >
                      <Select
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={estados}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              {!!selectedRowKeys.length

                && (
                  <Tabs
                    type="card"
                    activeKey={currentParentTabKey || tab1} // Evitar undefined
                    onChange={setCurrentParentTabKey}
                  >
                    <TabPane
                      tab={(
                        <span>
                          <InfoCircleOutlined />
                          {' '}
                          Periodos
                        </span>
                      )}
                      key={tab1}
                      forceRender
                    >
                      <ConfigurarGuardiasVacacionalesPeriodos
                        permission={getModelPermission('configurarguardiasvacacionales')}
                        idFather={selectedRowKeys}
                      />

                    </TabPane>
                    <TabPane
                      tab={(
                        <span>
                          <InfoCircleOutlined />
                          {' '}
                          Excepciones
                        </span>
                      )}
                      key={tab2}
                      forceRender
                    >
                      <ConfigurarGuardiasVacacionalesExcepciones
                        permission={permission}
                        idFather={selectedRowKeys}
                      />

                    </TabPane>

                  </Tabs>
                )}
            </Card>
          )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar ${nameModelSingular} ${registroName}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfigurarGuardiasVacacionalesGeneral;
